import { useState, useRef, useCallback, useEffect } from 'react';
import { VoiceActivityDetector, VAD_STATES } from './voiceActivityDetector';
import { QASessionManager, QA_STATES } from './qaStateMachine';

// Trailing silence (ms) before the VAD decides the user finished an answer.
// 2000ms was too aggressive — a normal thinking pause mid-answer tripped it and
// skipped the user before they were done. The final question uses a longer window
// because silence there COMPLETES the session and ends the recording (rather than
// just advancing), so a premature trigger loses the end of the last answer.
const SILENCE_DURATION_MS = 4000;
const SILENCE_DURATION_LAST_QUESTION_MS = 6000;

/**
 * useQASession - Custom hook for orchestrating Q&A Practice sessions
 *
 * Uses refs for audioContext and microphoneStream since these are created
 * AFTER recording starts, but VAD needs them later.
 */
const useQASession = ({
    audioContextRef,
    microphoneStreamRef,
    destinationNodeRef,
    playQuestionAudio,
    onSessionComplete
}) => {
    // State
    const [sessionState, setSessionState] = useState(QA_STATES.IDLE);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [progress, setProgress] = useState({ currentQuestion: 0, totalQuestions: 0, skippedCount: 0 });
    const [countdown, setCountdown] = useState(3);
    const [isInitialized, setIsInitialized] = useState(false);
    const [error, setError] = useState(null);
    const [sessionSummary, setSessionSummary] = useState(null);

    // Refs
    const sessionManagerRef = useRef(null);
    const vadRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const vadRetryTimeoutRef = useRef(null);
    // Dedicated AudioContext for the voice-activity detector. The detector must
    // NOT share the recording pipeline's AudioContext: that context already has
    // a MediaStreamSource for the mic (NewRecordPresentation), and Chrome
    // silences a SECOND createMediaStreamSource on the same context - so the
    // detector heard near-silence (peaks of -80 to -120 dB) and either skipped
    // questions or cut answers off on a momentary noise-floor graze while the
    // recording captured the real answer fine (console evidence, 2026-09-02).
    // A separate context is not subject to that mute.
    const vadAudioContextRef = useRef(null);

    /**
     * Initialize a new Q&A session with questions from backend
     */
    const initializeSession = useCallback((sessionData) => {
        console.log('useQASession: Initializing session', sessionData);

        if (!sessionData || !sessionData.questions || sessionData.questions.length === 0) {
            setError('No questions provided for session');
            return false;
        }

        // Create session manager with callbacks
        const manager = new QASessionManager(sessionData.questions, {
            onStateChange: (newState, oldState, state) => {
                console.log(`useQASession: State change ${oldState} -> ${newState}`);
                setSessionState(newState);

                setProgress({
                    currentQuestion: state.currentQuestionIndex + 1,
                    totalQuestions: state.totalQuestions,
                    skippedCount: state.answers.filter(a => a.skipped).length
                });
            },
            onQuestionStart: (question, progressData) => {
                console.log('useQASession: Question started', question);
                setCurrentQuestion(question);
            },
            onAwaitingAnswer: (question, progressData) => {
                console.log('useQASession: Awaiting answer for', question?.rephrasedQuestion);
                // Manual advance only (Toby, 2026-09-02): the voice-activity
                // detector is not started. Browser mic detection was unreliable
                // (a competing mic source read near-silence and either skipped
                // the question or cut the rep off mid-answer). The rep now
                // controls pacing with the "Next" button; the recording runs
                // continuously the whole time, so nothing is lost while they
                // take as long as they need. No auto-advance, no auto-skip.
            },
            onAnswerRecorded: (answer, progressData) => {
                console.log('useQASession: Answer recorded', answer);
                setProgress(progressData);
            },
            onQuestionSkipped: (answer, progressData) => {
                console.log('useQASession: Question skipped', answer);
                // Stop the VAD — the safety timeout bypasses VAD's own silence detection,
                // so the VAD is still running and would block the next question's startVAD().
                if (vadRef.current) {
                    vadRef.current.stop();
                    vadRef.current = null;
                }
                setProgress(progressData);
            },
            onSessionComplete: (summary) => {
                console.log('useQASession: Session complete', summary);
                setSessionSummary(summary);
                cleanup();
                if (onSessionComplete) {
                    onSessionComplete(summary);
                }
            },
            onError: (err) => {
                console.error('useQASession: Error', err);
                setError(err);
            }
        });

        manager.setSessionId(sessionData.sessionId);
        sessionManagerRef.current = manager;
        setIsInitialized(true);
        setError(null);

        setProgress({
            currentQuestion: 1,
            totalQuestions: sessionData.questions.length,
            skippedCount: 0
        });

        return true;
    }, [onSessionComplete]);

    /**
     * Start the Q&A session (triggers countdown then first question)
     */
    const startSession = useCallback(() => {
        if (!sessionManagerRef.current || !isInitialized) {
            console.error('useQASession: Session not initialized');
            return;
        }

        console.log('useQASession: Starting session');

        setCountdown(3);
        // Clear by the interval's OWN id: clearing via the ref from inside the
        // callback cleared whatever interval the ref pointed at by then, so a
        // second startSession orphaned the first interval for good (review,
        // 2026-09-04). Any interval already running is stopped first.
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }
        const countdownId = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(countdownId);
                    if (countdownIntervalRef.current === countdownId) {
                        countdownIntervalRef.current = null;
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        countdownIntervalRef.current = countdownId;

        sessionManagerRef.current.startSession(3);
    }, [isInitialized]);

    /**
     * Initialize and start Voice Activity Detection
     * Uses refs to get the CURRENT audioContext and microphoneStream.
     * Retries up to 3 times with delay if audio resources aren't ready yet.
     */
    const startVAD = useCallback((retryCount = 0, isLastQuestion = false) => {
        const mic = microphoneStreamRef?.current;

        // The detector runs on its OWN AudioContext (see vadAudioContextRef),
        // created lazily and reused across questions, so it never collides with
        // the recording context's mic source. Only the mic stream must be ready.
        if (mic) {
            if (!vadAudioContextRef.current || vadAudioContextRef.current.state === 'closed') {
                vadAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            // A fresh context can start suspended until a user gesture; the
            // session starts from a click, so resume() resolves immediately.
            if (vadAudioContextRef.current.state === 'suspended') {
                vadAudioContextRef.current.resume().catch(() => {});
            }
        }
        const ctx = vadAudioContextRef.current;

        if (!ctx || !mic) {
            if (retryCount < 3) {
                console.warn(`useQASession: VAD resources not ready, retrying in 500ms (attempt ${retryCount + 1}/3)`,
                    { hasCtx: !!ctx, hasMic: !!mic });
                // Tracked so cleanup can cancel it. Left dangling, this fires after
                // unmount, clones the mic, and the clone is orphaned live: VAD.start()
                // stores the stream before createMediaStreamSource throws on the
                // already-closed AudioContext, and that throw is swallowed.
                vadRetryTimeoutRef.current = setTimeout(
                    () => startVAD(retryCount + 1, isLastQuestion), 500);
                return;
            }
            console.error('useQASession: Cannot start VAD after 3 retries - missing audioContext or microphoneStream',
                { hasCtx: !!ctx, hasMic: !!mic });
            // Surface it: with no VAD, nothing ever advances a question except
            // the 90s safety timeout, which records every answer as SKIPPED —
            // the whole session silently degrades to dead-air blocks.
            setError('Microphone monitoring failed to start. Please restart the session — your answers cannot be detected.');
            return;
        }

        // Always stop and discard any existing VAD before creating a fresh one.
        // This prevents "Already running" errors when a previous question's VAD
        // wasn't properly cleaned up (e.g., safety timeout skipped the question).
        if (vadRef.current) {
            vadRef.current.stop();
            vadRef.current = null;
        }

        vadRef.current = new VoiceActivityDetector(ctx, {
            silenceThreshold: -50,
            silenceDuration: isLastQuestion ? SILENCE_DURATION_LAST_QUESTION_MS : SILENCE_DURATION_MS,
            noSpeechTimeout: 30000,
            minSpeechDuration: 500
        });

        vadRef.current.setCallbacks({
            onSpeechStart: () => {
                console.log('useQASession: Speech started');
                if (sessionManagerRef.current) {
                    sessionManagerRef.current.notifySpeechDetected();
                }
            },
            onSpeechEnd: () => {
                console.log('useQASession: Speech ended');
            },
            onSilenceDetected: (data) => {
                console.log('useQASession: Silence detected', data);
                vadRef.current = null;
                if (sessionManagerRef.current) {
                    sessionManagerRef.current.notifySilenceDetected(data);
                }
            },
            onTimeout: (data) => {
                console.log('useQASession: VAD timeout', data);
                vadRef.current = null;
                if (sessionManagerRef.current) {
                    sessionManagerRef.current.notifyTimeout(data);
                }
            },
            onStateChange: (newState, oldState) => {
                console.log(`useQASession: VAD state ${oldState} -> ${newState}`);
            }
        });

        // Give the detector a cloned track on its dedicated context. The clone
        // is an independent track (unaffected when the recording stream stops);
        // on a separate context there is no second-source mute to work around.
        vadRef.current.start(mic.clone());
        console.log('useQASession: VAD started');
    }, [microphoneStreamRef]);

    const stopVAD = useCallback(() => {
        if (vadRef.current) {
            vadRef.current.stop();
            console.log('useQASession: VAD stopped');
        }
    }, []);

    const pauseVAD = useCallback(() => {
        if (vadRef.current) {
            vadRef.current.pause();
        }
    }, []);

    const resumeVAD = useCallback(() => {
        if (vadRef.current) {
            vadRef.current.resume();
        }
    }, []);

    const notifyTTSStarted = useCallback(() => {
        if (sessionManagerRef.current) {
            sessionManagerRef.current.notifyTTSStarted();
        }
        pauseVAD();
    }, [pauseVAD]);

    const notifyTTSEnded = useCallback(() => {
        if (sessionManagerRef.current) {
            sessionManagerRef.current.notifyTTSEnded();
        }
    }, []);

    const nextQuestion = useCallback(() => {
        stopVAD();
        if (sessionManagerRef.current) {
            sessionManagerRef.current.notifySilenceDetected({ reason: 'manual_next' });
        }
    }, [stopVAD]);

    const skipQuestion = useCallback(() => {
        stopVAD();
        if (sessionManagerRef.current) {
            sessionManagerRef.current.notifyTimeout({ reason: 'manual_skip' });
        }
    }, [stopVAD]);

    const endSessionEarly = useCallback(() => {
        stopVAD();
        if (sessionManagerRef.current) {
            sessionManagerRef.current.completeSession();
        }
    }, [stopVAD]);

    const resetSession = useCallback(() => {
        cleanup();
        setSessionState(QA_STATES.IDLE);
        setCurrentQuestion(null);
        setProgress({ currentQuestion: 0, totalQuestions: 0, skippedCount: 0 });
        setCountdown(3);
        setIsInitialized(false);
        setError(null);
        setSessionSummary(null);
    }, []);

    const cleanup = useCallback(() => {
        if (vadRef.current) {
            vadRef.current.stop();
            vadRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        if (vadRetryTimeoutRef.current) {
            clearTimeout(vadRetryTimeoutRef.current);
            vadRetryTimeoutRef.current = null;
        }
        // Close the detector's dedicated AudioContext so it is not leaked -
        // browsers cap live AudioContexts (~6), and a session opens a fresh one.
        if (vadAudioContextRef.current && vadAudioContextRef.current.state !== 'closed') {
            vadAudioContextRef.current.close().catch(() => {});
            vadAudioContextRef.current = null;
        }
        if (sessionManagerRef.current) {
            sessionManagerRef.current.cleanup();
        }
    }, []);

    useEffect(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);

    const getVADState = useCallback(() => {
        // ?? not ||: a legitimately falsy state value must not collapse to IDLE
        return vadRef.current?.getState() ?? VAD_STATES.IDLE;
    }, []);

    const getCurrentAudioLevel = useCallback(() => {
        // ?? not ||: a level of exactly 0 dBFS (clipping-loud) is falsy and
        // would read as -Infinity — an empty meter at maximum input.
        return vadRef.current?.getCurrentLevel() ?? -Infinity;
    }, []);

    return {
        sessionState,
        currentQuestion,
        progress,
        countdown,
        isInitialized,
        error,
        sessionSummary,

        initializeSession,
        startSession,
        notifyTTSStarted,
        notifyTTSEnded,
        nextQuestion,
        skipQuestion,
        endSessionEarly,
        resetSession,

        startVAD,
        stopVAD,
        pauseVAD,
        resumeVAD,
        getVADState,
        getCurrentAudioLevel
    };
};

export default useQASession;
